import { useState, useEffect, useRef } from "react";
import { PROFILE_QUESTIONS, SCORED_QUESTIONS, SCORE_DIMENSIONS, JURISDICTIONS, G7_JURISDICTIONS, computeScores, band, preparedness, generateFindings } from './quizConfig';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const BOOKING_URL  = "https://calendly.com/d/cwk4-cpy-gyt/quantum-resistance-call";
const WEBSITE_URL  = "https://horizenlabs.io";
const COMPANY_NAME = "Horizen Labs";
const API_BASE     = window.location.hostname === "horizenlabs.io"
  ? "/quantum-security/risk-check"
  : "";

const LOGO_URL     = "https://horizenlabs.io/logo.svg";

// Brand palette
const C = {
  bg:       "#FFFFFF",
  text:     "#041742",   // dark blue — primary text
  teal:     "#4CDFDD",   // accent
  lavender: "#BBC0FF",   // accent
  magenta:  "#F251EA",   // small highlights only
  // System colors for risk states (used minimally)
  textMuted:    "#5A6580",
  textSubtle:   "#8B95AD",
  border:       "#E5E8F0",
  borderStrong: "#041742",
  surface:      "#F7F8FB",
  surfaceTeal:    "#E8FBFA",
  surfaceLavender:"#F0F2FF",
  red:    "#C8102E",
  amber:  "#EAB308",
  green:  "#0F7A4A",
};

// Typography
const F = {
  display: "'IBM Plex Sans', sans-serif",
  body:    "'Roboto', sans-serif",
  mono:    "'IBM Plex Mono', monospace",
};

const TERMS_AND_CONDITIONS = [
  {
    title: "QUANTUM READINESS ASSESSMENT TERMS OF USE",
    body: `Last Updated: May 15, 2026. 
These Terms of Use (these “Terms”) govern your use of the Quantum Readiness Assessment Tool (the  “Tool”) provided by Horizen Labs, Inc. (“Horizen Labs,” “we,” “us” or “our”). By accessing or using the Tool, you agree to be bound by these Terms.
For purposes of these Terms, “you” and “your” means you as the user of the Tool. If you use the Tool on behalf of a company or other entity, then “you” includes you and that entity, and you represent and warrant that (a) you are an authorized representative of the entity with the authority to bind the entity to these Terms, and (b) you agree to these Terms on the entity’s behalf.`,
  },
  {
    title: "1. MARKETING AND EDUCATIONAL PURPOSES ONLY",
    body: `The Tool is designed to provide a high-level, preliminary overview of quantum readiness based on generalized industry trends and user-provided inputs. It is intended for marketing and educational purposes only. 
<br/>
The results, scores, or suggestions generated by the Tool are illustrative examples and do not constitute a comprehensive security audit, professional advice, or a definitive roadmap for quantum migration.
`,
  },
  {
    title: "2. NO RELIANCE",
    body: `You acknowledge and agree that: (i) the results provided by the Tool are based on limited data and simplified algorithms; (ii) the Tool is not to be relied upon for making business, financial, technical, or security decisions; (iii) an actual quantum readiness assessment requires a deep-dive technical investigation and evaluation of your specific infrastructure, cryptographic inventory, and data sensitivity which this Tool does not provide in any form.`,
  },
  {
    title: "3. DISCLAIMER OF WARRANTIES",
    body: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TOOL IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF ANY KIND. HORIZEN LABS EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO: (I) ANY WARRANTIES OF ACCURACY, COMPLETENESS, OR RELIABILITY OF THE ASSESSMENT RESULTS; (II) ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT; AND (III) ANY WARRANTY THAT THE TOOL WILL BE ERROR-FREE OR UNINTERRUPTED.`,
  },
  {
    title: "4. LIMITATION OF LIABILITY",
    body: `IN NO EVENT SHALL HORIZEN LABS, ITS AFFILIATES, OR THEIR RESPECTIVE OFFICERS, DIRECTORS AND EMPLOYEES BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES (INCLUDING, WITHOUT LIMITATION, LOSS OF DATA, REVENUE, OR PROFITS) ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR RELIANCE ON THE TOOL, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
<br/>
NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, HORIZEN LABS’ TOTAL AGGREGATE LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM OR RELATED TO THE USE OF THIS TOOL, REGARDLESS OF THE FORM OF ACTION, WILL AT ALL TIMES BE LIMITED TO ONE HUNDRED DOLLARS ($100.00 USD).
`,
  },
  {
    title: "5. USE OF PROVIDED CONTACT INFORMATION",
    body: `By using this Tool, you may choose to provide your contact information to Horizen Labs (e.g., email address, company name). You agree that Horizen Labs may use this information to contact you regarding your results and other services offered by Horizen Labs. All data provided is subject to our Privacy Policy available at https://horizenlabs.io/privacy. `,
  },
  {
    title: "6. INTELLECTUAL PROPERTY",
    body: `The Tool, including its design, logic, and generated content, is the intellectual property of Horizen Labs. You are granted a limited, non-exclusive, non-transferable license to use the Tool for your own internal, non-commercial evaluation purposes.`,
  },
  {
    title: "7. NO CONFIDENTIALITY OBLIGATIONS",
    body: `By using the Tool, you agree that: (i) you will not input any sensitive, proprietary, or confidential information into the Tool; (ii) Horizen Labs has no obligation of confidentiality, express or implied, with respect to any information you provide through the Tool; and (iii) information provided by you in completing the Tool is for the purpose of generating a general report and facilitating further discussion with Horizen Labs. If you require a confidential engagement, you must enter into a separate, signed Non-Disclosure Agreement (NDA) with us. Use of this Tool does not create a confidential relationship between you and Horizen Labs.`,
  },
  {
    title: "8. MODIFICATIONS",
    body: `Horizen Labs reserves the right to modify, suspend, or discontinue the Tool at any time without notice. Horizen Labs may modify, amend, or update these Terms at any time, with or without notice to you, by posting the updated Terms on the Tool’s webpage or otherwise making them available to you. Your continued use of the Tool after such posting constitutes acceptance of the updated Terms. If you do not agree to be bound by the updated Terms, then you may no longer utilize the Tool.
<br/>
You acknowledge that the field of quantum computing and post-quantum cryptography is rapidly evolving. The results provided by this Tool reflect a snapshot in time based on currently available public information. Horizen Labs is under no obligation to update the Tool or your specific results as new cryptographic standards or quantum capabilities emerge.
`,
  },
{
    title: "9. GOVERNING LAW",
    body: `These Terms, and all matters, disputes or controversies arising out of or relating hereto, shall be governed by the laws of the State of New York (irrespective of choice of law principles). At Horizen Lab’s sole discretion, it may require you to submit any disputes arising from the use of these Terms to final and binding arbitration under the American Arbitration Association in New York, New York. ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO THESE TERMS MUST BE COMMENCED WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES, OTHERWISE, SUCH CAUSE OF ACTION OR CLAIM IS PERMANENTLY BARRED.
`,
  },


];




function trackClick(clickType, linkName) {
  if (typeof window.gtag === "function") {
    window.gtag("event", "click", { click_type: clickType, link_name: linkName, site: "General Quantum Risk Check" });
  }
}

function findingColor(kind) {
  if (kind === "alert") return C.red;
  if (kind === "warn")  return C.amber;
  return C.text; // info — use dark blue
}

// ─────────────────────────────────────────────────────────────────────────────
// EMAIL / URL HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function encodeAnswers(answers) {
  try { return btoa(JSON.stringify(answers)); } catch { return ""; }
}

function decodeAnswers(encoded) {
  try { return JSON.parse(atob(encoded)); } catch { return null; }
}

function buildResultsUrl(answers) {
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("r", encodeAnswers(answers));
  return url.toString();
}

async function sendResultsEmail(userEmail, resultsUrl) {
  console.log("[Beehiiv] Sending results:", userEmail, resultsUrl);
  try {
    const res = await fetch(`${API_BASE}/api/send-results`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail, resultsUrl }),
    });
    const data = await res.json();
    if (res.ok) {
      console.log("[Beehiiv] Success:", data);
    } else {
      console.error("[Beehiiv] API error:", res.status, data);
    }
  } catch (err) {
    console.error("[Beehiiv] Fetch failed:", err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const ALL_QUESTIONS = [...PROFILE_QUESTIONS, ...SCORED_QUESTIONS];
const TOTAL_STEPS   = ALL_QUESTIONS.length;

function Eyebrow({ children, color = C.text }) {
  return (
    <div style={{ fontSize: "11px", color, letterSpacing: "0.14em", fontFamily: F.mono, fontWeight: 500, textTransform: "uppercase" }}>
      {children}
    </div>
  );
}

function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div style={{ marginBottom: "2.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <Eyebrow color={C.textMuted}>QUESTION {current} / {total}</Eyebrow>
        <span style={{ fontSize: "11px", color: C.textMuted, fontFamily: F.mono, letterSpacing: "0.05em" }}>{pct}%</span>
      </div>
      <div style={{ height: "2px", background: C.border, position: "relative" }}>
        <div style={{
          position: "absolute", inset: 0, width: `${pct}%`, background: C.text,
          transition: "width 0.4s cubic-bezier(0.4,0,0.2,1)"
        }} />
      </div>
    </div>
  );
}

function OptionButton({ label, selected, onClick, multi }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: "14px",
        width: "100%", padding: "16px 18px", marginBottom: "8px",
        background: selected ? C.surfaceTeal : C.bg,
        border: `1px solid ${selected ? C.text : C.border}`,
        borderRadius: "4px", cursor: "pointer", textAlign: "left",
        transition: "all 0.15s",
        fontFamily: F.body,
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = C.lavender; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = C.border; }}
    >
      <div style={{
        flexShrink: 0, width: "18px", height: "18px",
        borderRadius: multi ? "2px" : "50%",
        border: `1.5px solid ${selected ? C.text : "#C5CAD7"}`,
        background: selected ? C.text : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {selected && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke={C.teal} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <span style={{ fontSize: "15px", color: C.text, lineHeight: 1.45, fontWeight: selected ? 500 : 400 }}>
        {label}
      </span>
    </button>
  );
}

function DimBar({ label, score, delay }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(score), 100 + delay);
    return () => clearTimeout(t);
  }, [score, delay]);
  const b = band(score);
  return (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px" }}>
        <span style={{ fontSize: "14px", color: C.text, fontFamily: F.body, fontWeight: 500 }}>{label}</span>
        <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          <span style={{ fontSize: "11px", color: b.color, fontFamily: F.mono, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {b.label}
          </span>
          <span style={{ fontSize: "16px", fontWeight: 600, color: C.text, fontFamily: F.mono }}>{score}</span>
        </div>
      </div>
      <div style={{ height: "4px", background: C.border, position: "relative" }}>
        <div style={{
          position: "absolute", inset: 0, width: `${w}%`, background: b.color,
          transition: "width 0.9s cubic-bezier(0.4,0,0.2,1)"
        }} />
      </div>
    </div>
  );
}

function ScoreRing({ score }) {
  const [displayed, setDisplayed] = useState(0);
  const [offset, setOffset]       = useState(339.3);
  const b = band(score);
  const r = 60, circ = 2 * Math.PI * r;

  useEffect(() => {
    let start = null;
    const animate = (ts) => {
      if (!start) start = ts;
      const p    = Math.min((ts - start) / 1200, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplayed(Math.round(score * ease));
      setOffset(circ - circ * (score / 100) * ease);
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [score, circ]);

  return (
    <div style={{ position: "relative", width: "144px", height: "144px", flexShrink: 0 }}>
      <svg width="144" height="144" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="72" cy="72" r={r} fill="none" stroke={C.border} strokeWidth="6" />
        <circle cx="72" cy="72" r={r} fill="none" stroke={b.color} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="butt" />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: "40px", fontWeight: 600, color: C.text, lineHeight: 1, fontFamily: F.display, letterSpacing: "-0.02em" }}>
          {displayed}
        </span>
        <span style={{ fontSize: "10px", color: C.textSubtle, letterSpacing: "0.1em", fontFamily: F.mono, marginTop: "4px" }}>
          / 100
        </span>
      </div>
    </div>
  );
}

function TermsModal({ scrolledToEnd, onScrolledToEnd, accepted, onAcceptedChange, onClose, onConfirm }) {
  const scrollRef = useRef(null);
  const titleId = "terms-modal-title";

  function checkScrollEnd(el) {
    if (!el) return;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 8) {
      onScrolledToEnd();
    }
  }

  useEffect(() => {
    checkScrollEnd(scrollRef.current);
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(4, 23, 66, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "560px", maxHeight: "85vh",
          background: C.bg, border: `1px solid ${C.border}`,
          display: "flex", flexDirection: "column",
          boxShadow: "0 24px 48px rgba(4, 23, 66, 0.18)",
        }}
      >
        <div style={{ padding: "1.25rem 1.5rem", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <h2
            id={titleId}
            style={{
              fontFamily: F.display, fontSize: "22px", fontWeight: 600,
              color: C.text, margin: 0, letterSpacing: "-0.02em",
            }}
          >
            Terms &amp; Conditions
          </h2>
        </div>

        <div
          ref={scrollRef}
          onScroll={e => checkScrollEnd(e.currentTarget)}
          style={{
            flex: 1, overflowY: "auto", padding: "1.25rem 1.5rem",
            minHeight: 0,
          }}
        >
          {TERMS_AND_CONDITIONS.map((section, i) => (
            <div key={i} style={{ marginBottom: i === TERMS_AND_CONDITIONS.length - 1 ? 0 : "1.25rem" }}>
              <h3 style={{
                fontFamily: F.display, fontSize: "14px", fontWeight: 600,
                color: C.text, margin: "0 0 8px", letterSpacing: "-0.01em",
              }}>
                {section.title}
              </h3>
              <p style={{
                fontSize: "13px", color: C.textMuted, margin: 0,
                lineHeight: 1.65, fontFamily: F.body,
              }}>
                {section.body}
              </p>
            </div>
          ))}
        </div>

        <div style={{
          padding: "1rem 1.5rem 1.25rem",
          borderTop: `1px solid ${C.border}`,
          flexShrink: 0,
          background: C.surface,
        }}>
          {!scrolledToEnd && (
            <p style={{
              fontSize: "12px", color: C.textSubtle, margin: "0 0 12px",
              fontFamily: F.body, lineHeight: 1.5,
            }}>
              Please scroll to the bottom to continue.
            </p>
          )}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: "10px",
            cursor: scrolledToEnd ? "pointer" : "not-allowed",
            marginBottom: "1rem", opacity: scrolledToEnd ? 1 : 0.5,
          }}>
            <input
              type="checkbox"
              checked={accepted}
              disabled={!scrolledToEnd}
              onChange={e => onAcceptedChange(e.target.checked)}
              style={{ marginTop: "3px", flexShrink: 0, accentColor: C.text }}
            />
            <span style={{ fontSize: "13px", color: C.text, lineHeight: 1.5, fontFamily: F.body }}>
              Yes, I agree to these Terms &amp; Conditions
            </span>
          </label>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!accepted}
            style={{
              width: "100%", padding: "14px",
              background: accepted ? C.text : C.surface,
              color: accepted ? C.bg : C.textSubtle,
              border: `1px solid ${accepted ? C.text : C.border}`,
              borderRadius: "4px", fontSize: "15px", fontWeight: 500,
              cursor: accepted ? "pointer" : "default",
              fontFamily: F.body, marginBottom: "8px",
            }}
          >
            View my results →
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%", padding: "10px",
              background: "transparent", border: "none",
              color: C.textMuted, fontSize: "13px", cursor: "pointer",
              fontFamily: F.body, textDecoration: "underline",
              textDecorationColor: C.border, textUnderlineOffset: "3px",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [phase,   setPhase]   = useState("intro");
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim());

  const [step,    setStep]    = useState(0);
  const [answers, setAnswers] = useState({});
  const [email,   setEmail]   = useState("");
  const [termsModalOpen, setTermsModalOpen]       = useState(false);
  const [termsScrolledToEnd, setTermsScrolledToEnd] = useState(false);
  const [termsAccepted, setTermsAccepted]         = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [pdfLoading, setPdfLoading] = useState(false);
  const topRef = useRef(null);

  const currentQ  = ALL_QUESTIONS[step];
  const isProfile = step < PROFILE_QUESTIONS.length;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const r = new URLSearchParams(window.location.search).get("r");
    if (!r) return;
    const decoded = decodeAnswers(r);
    if (decoded) {
      setAnswers(decoded);
      setPhase("results");
    }
  }, []);

  useEffect(() => {
    if (phase === "intro")      trackClick("quiz_funnel", "intro");
    else if (phase === "questions") trackClick("quiz_funnel", `question_${step + 1}`);
    else if (phase === "email") trackClick("quiz_funnel", "email_gate");
    else if (phase === "results") trackClick("quiz_funnel", "results");
  }, [phase, step]);

  function scrollTop() {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function openTermsModal() {
    setTermsScrolledToEnd(false);
    setTermsAccepted(false);
    setTermsModalOpen(true);
  }

  function closeTermsModal() {
    setTermsModalOpen(false);
    setTermsScrolledToEnd(false);
    setTermsAccepted(false);
  }

  function confirmTermsAndViewResults() {
    if (!termsAccepted) return;
    trackClick("quiz_funnel", email && isValidEmail(email) ? "terms_accepted_with_email" : "terms_accepted_skipped");
    setTermsModalOpen(false);
    setTermsScrolledToEnd(false);
    setTermsAccepted(false);
    if (email && isValidEmail(email)) {
      sendResultsEmail(email, buildResultsUrl(answers));
    }
    setPhase("results");
    scrollTop();
  }

  function handleSingle(qid, value) {
    setAnswers(a => ({ ...a, [qid]: value }));
  }

  function handleMulti(qid, value, exclusive) {
    setAnswers(a => {
      const prev = a[qid] || [];
      if (exclusive) {
        return { ...a, [qid]: prev.includes(value) ? [] : [value] };
      }
      const withoutExclusives = prev.filter(v => {
        const opt = SCORED_QUESTIONS.find(q => q.id === qid)?.options.find(o => o.value === v);
        return !opt?.exclusive;
      });
      return {
        ...a,
        [qid]: withoutExclusives.includes(value)
          ? withoutExclusives.filter(v => v !== value)
          : [...withoutExclusives, value],
      };
    });
  }

  function canAdvance() {
    const ans = answers[currentQ.id];
    if (currentQ.type === "single") return !!ans;
    return ans && ans.length > 0;
  }

  function advance() {
    scrollTop();
    if (step < ALL_QUESTIONS.length - 1) setStep(s => s + 1);
    else setPhase("email");
  }

  function back() {
    scrollTop();
    if (step > 0) setStep(s => s - 1);
    else setPhase("intro");
  }

  function resetAll() {
    setAnswers({});
    setStep(0);
    setEmail("");
    setTermsModalOpen(false);
    setTermsScrolledToEnd(false);
    setTermsAccepted(false);
    setPhase("intro");
    scrollTop();
  }

  async function downloadPDF() {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scores, findings, ob, prep, regulatory: activeJurisdictions.map(key => JURISDICTIONS[key]) }),
      });
      if (!res.ok) throw new Error(`PDF generation failed: ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "quantum-readiness-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[downloadPDF]", err);
    } finally {
      setPdfLoading(false);
    }
  }

  const scores   = computeScores(answers);
  const findings = generateFindings(answers, scores);
  const ob       = band(scores.overall);
  const prep     = preparedness(scores.readiness, scores.overall);
  const activeJurisdictions = (answers.jurisdictions || []).filter(j => JURISDICTIONS[j]);
  if (activeJurisdictions.some(j => G7_JURISDICTIONS.includes(j))) {
    activeJurisdictions.push("g7");
  }

  // Wrappers
  const wrap = (children) => (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: F.body,
    }}>
      <div ref={topRef} />
      <div style={{ maxWidth: "820px", margin: "0 auto", padding: "3rem 1.5rem" }}>
        {children}
      </div>
    </div>
  );

  // Logo: will refer to the top
  const logo = (
    <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer"
      style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
      aria-label={`${COMPANY_NAME} — visit website`}
      onClick={() => trackClick("nav_click", "header_logo")}>
      <img src={LOGO_URL} alt={`${COMPANY_NAME} logo`}
        style={{ height: "32px", width: "auto", display: "block" }} />
    </a>
  );

  // Footer: brand link back to main site, plus thin legal/credit row.
  const footer = (
    <footer style={{
      marginTop: "4rem",
      paddingTop: "2rem",
      borderTop: `1px solid ${C.border}`,
    }}>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "1.5rem",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1.25rem",
      }}>
        <a
          href={WEBSITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            textDecoration: "none",
            color: C.text,
          }}
          aria-label={`${COMPANY_NAME} website`}
          onClick={() => trackClick("nav_click", "footer_logo")}
        >
          <span style={{ fontFamily: F.body, fontSize: "13px", color: C.textMuted }}>Built by</span>
          <img
            src={LOGO_URL}
            alt={`${COMPANY_NAME} logo`}
            style={{ height: "26px", width: "auto", display: "block", objectFit: "contain" }}
          />
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M7 17L17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </a>
        <div style={{ display: "flex", gap: "1.5rem" }}>
          <a href={`${WEBSITE_URL}/about-us`} target="_blank" rel="noopener noreferrer"
            style={{ color: C.textMuted, textDecoration: "none", fontFamily: F.body, fontSize: "13px" }}
            onClick={() => trackClick("nav_click", "footer_about")}>About</a>
          <a href="https://horizenlabs.io/quantum-security" target="_blank" rel="noopener noreferrer"
            style={{ color: C.textMuted, textDecoration: "none", fontFamily: F.body, fontSize: "13px" }}
            onClick={() => trackClick("nav_click", "footer_consulting")}>Consulting</a>
          <a href="https://horizenlabs.io/contact" target="_blank" rel="noopener noreferrer"
            style={{ color: C.textMuted, textDecoration: "none", fontFamily: F.body, fontSize: "13px" }}
            onClick={() => trackClick("nav_click", "footer_contact")}>Contact</a>
          <a href={`${WEBSITE_URL}/privacy`} target="_blank" rel="noopener noreferrer"
            style={{ color: C.textMuted, textDecoration: "none", fontFamily: F.body, fontSize: "13px" }}
            onClick={() => trackClick("nav_click", "footer_privacy")}>Privacy</a>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: F.mono, fontSize: "11px", color: C.textSubtle, letterSpacing: "0.06em" }}>
          © {new Date().getFullYear()} {COMPANY_NAME.toUpperCase()}
        </span>
        <span style={{ fontFamily: F.mono, fontSize: "11px", color: C.textSubtle, letterSpacing: "0.06em" }}>
          QUANTUM RISK CHECK
        </span>
      </div>
    </footer>
  );

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === "intro") return wrap(<>
    <div style={{ marginBottom: "3rem" }}>{logo}</div>
    <Eyebrow color={C.textMuted}>QUANTUM RISK CHECK</Eyebrow>
    <h1 style={{
      fontFamily: F.display, fontSize: "clamp(32px, 5.5vw, 48px)", fontWeight: 600,
      lineHeight: 1.1, color: C.text, margin: "16px 0 1.25rem", letterSpacing: "-0.025em",
    }}>
      Is your organisation<br />
      <span style={{ position: "relative", display: "inline-block" }}>
        quantum-safe?
        <span style={{ position: "absolute", left: 0, right: 0, bottom: "2px", height: "10px", background: C.teal, opacity: 0.5, zIndex: -1 }} />
      </span>
    </h1>
    <p style={{ fontSize: "16px", color: C.textMuted, lineHeight: 1.65, margin: "0 0 2.5rem", maxWidth: "560px" }}>
      Quantum computers are on track to break the encryption protecting your data, transactions and systems on a timeline most organisations are not yet prepared for. Take our free 5-minute Risk Check to find out where you stand.
    </p>

    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "1px", background: C.border, marginBottom: "2.5rem", border: `1px solid ${C.border}` }}>
      {[["10", "questions"], ["5 min", "to complete"], ["Free", "instant score"]].map(([n, l]) => (
        <div key={n} style={{ background: C.bg, padding: "20px 12px", textAlign: "center" }}>
          <div style={{ fontSize: "22px", fontWeight: 600, color: C.text, fontFamily: F.display, letterSpacing: "-0.01em" }}>{n}</div>
          <div style={{ fontSize: "12px", color: C.textMuted, marginTop: "4px", fontFamily: F.body }}>{l}</div>
        </div>
      ))}
    </div>

    <div style={{ borderLeft: `2px solid ${C.teal}`, paddingLeft: "16px", marginBottom: "2.5rem" }}>
      <Eyebrow color={C.text}>WHAT YOU'LL GET</Eyebrow>
      <p style={{ margin: "8px 0 0", fontSize: "14px", color: C.text, lineHeight: 1.6 }}>
        A personalised Quantum Risk Score across three dimensions — Cryptographic Exposure, Harvest Risk, and Signature Integrity — plus regulatory flags and recommended next steps.
      </p>
    </div>

    <button onClick={() => { setPhase("questions"); scrollTop(); }}
      style={{
        width: "100%", padding: "18px",
        background: C.text, color: C.bg,
        border: "none", borderRadius: "4px",
        fontSize: "15px", fontWeight: 500, cursor: "pointer",
        fontFamily: F.body, letterSpacing: "0.01em",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#0A2461"}
      onMouseLeave={e => e.currentTarget.style.background = C.text}
    >
      Start free quiz →
    </button>
    <p style={{ textAlign: "center", fontSize: "12px", color: C.textSubtle, marginTop: "16px", fontFamily: F.mono, letterSpacing: "0.04em" }}>
      No account required — instant score
    </p>
    {footer}
  </>);

  // ── QUESTIONS ──────────────────────────────────────────────────────────────
  if (phase === "questions") return wrap(<>
    <div style={{ marginBottom: "3rem" }}>{logo}</div>
    <ProgressBar current={step + 1} total={TOTAL_STEPS} />

    <Eyebrow color={isProfile ? C.textMuted : C.text}>
      {isProfile ? "YOUR PROFILE" : currentQ.section}
    </Eyebrow>

    <h2 style={{
      fontFamily: F.display, fontSize: "24px", fontWeight: 500,
      color: C.text, margin: "12px 0 8px", lineHeight: 1.3, letterSpacing: "-0.015em",
    }}>
      {currentQ.label}
    </h2>
    {currentQ.hint
      ? <p style={{ fontSize: "13px", color: C.textMuted, margin: "0 0 1.5rem", fontFamily: F.body }}>{currentQ.hint}</p>
      : <div style={{ height: "1.5rem" }} />}

    <div>
      {currentQ.options.map(opt => {
        const ans = answers[currentQ.id];
        const selected = currentQ.type === "single" ? ans === opt.value : (ans || []).includes(opt.value);
        return (
          <OptionButton key={opt.value} label={opt.label} selected={selected} multi={currentQ.type === "multi"}
            onClick={() => currentQ.type === "single"
              ? handleSingle(currentQ.id, opt.value)
              : handleMulti(currentQ.id, opt.value, opt.exclusive)} />
        );
      })}
    </div>

    <div style={{ display: "flex", gap: "10px", marginTop: "2rem" }}>
      <button onClick={back}
        style={{
          padding: "14px 24px", background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: "4px", color: C.textMuted, cursor: "pointer", fontSize: "14px",
          fontFamily: F.body, transition: "all 0.15s"
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.text; e.currentTarget.style.color = C.text; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}
      >
        ← Back
      </button>
      <button onClick={advance} disabled={!canAdvance()}
        style={{
          flex: 1, padding: "14px",
          background: canAdvance() ? C.text : C.surface,
          color: canAdvance() ? C.bg : C.textSubtle,
          border: "none", borderRadius: "4px",
          fontSize: "15px", fontWeight: 500,
          cursor: canAdvance() ? "pointer" : "default",
          fontFamily: F.body, transition: "all 0.15s",
        }}>
        {step === ALL_QUESTIONS.length - 1 ? "See my results →" : "Continue →"}
      </button>
    </div>
    {footer}
  </>);

  // ── EMAIL GATE ─────────────────────────────────────────────────────────────
  if (phase === "email") return wrap(<>
    <div style={{ marginBottom: "3rem" }}>{logo}</div>
    <div style={{ marginBottom: "2.5rem" }}>
      <div style={{ width: "44px", height: "44px", background: C.surfaceTeal, border: `1px solid ${C.teal}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.5rem" }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M5 13l4 4L19 7" stroke={C.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <Eyebrow color={C.textMuted}>QUIZ COMPLETE</Eyebrow>
      <h2 style={{ fontFamily: F.display, fontSize: "30px", fontWeight: 600, color: C.text, margin: "10px 0 8px", letterSpacing: "-0.02em" }}>
        Your Quantum Risk Score is ready
      </h2>
      <p style={{ fontSize: "15px", color: C.textMuted, margin: 0, fontFamily: F.body, lineHeight: 1.6 }}>
        Enter your work email to unlock your full results. We'll also send you a copy to share with your team.
      </p>
    </div>

    <div style={{ background: C.surface, padding: "1.5rem", marginBottom: "1.5rem", border: `1px solid ${C.border}` }}>
      <Eyebrow color={C.text}>YOU'LL RECEIVE</Eyebrow>
      <div style={{ marginTop: "12px" }}>
        {[
          "Personalised Quantum Risk Score",
          "Key findings specific to your profile",
          "Regulatory flags for your sector",
          "Recommended immediate next steps",
        ].map(item => (
          <div key={item} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
            <div style={{ width: "5px", height: "5px", background: C.text, flexShrink: 0 }} />
            <span style={{ fontSize: "14px", color: C.text, fontFamily: F.body }}>{item}</span>
          </div>
        ))}
      </div>
    </div>

    <input type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)}
      onKeyDown={e => { if (e.key === "Enter" && isValidEmail(email)) openTermsModal(); }}
      style={{
        width: "100%", padding: "16px", background: C.bg,
        border: `1px solid ${isValidEmail(email) ? C.text : C.border}`,
        color: C.text, fontSize: "15px", marginBottom: "10px",
        fontFamily: F.body, borderRadius: "4px", outline: "none",
        transition: "border 0.15s",
      }}
    />
    <button onClick={() => { if (isValidEmail(email)) { trackClick("quiz_funnel", "terms_opened_with_email"); openTermsModal(); } }} disabled={!isValidEmail(email)}
      style={{
        width: "100%", padding: "16px",
        background: isValidEmail(email) ? C.text : C.surface,
        color: isValidEmail(email) ? C.bg : C.textSubtle,
        border: "none", borderRadius: "4px",
        fontSize: "15px", fontWeight: 500,
        cursor: isValidEmail(email) ? "pointer" : "default",
        fontFamily: F.body, marginBottom: "10px",
      }}>
      View my results →
    </button>
    <button onClick={() => { trackClick("quiz_funnel", "terms_opened_skipped"); openTermsModal(); }}
      style={{ width: "100%", padding: "10px", background: "transparent", border: "none", color: C.textSubtle, fontSize: "13px", cursor: "pointer", fontFamily: F.body, textDecoration: "underline", textDecorationColor: C.border, textUnderlineOffset: "3px" }}>
      Skip — show score without saving
    </button>

    {termsModalOpen && (
      <TermsModal
        scrolledToEnd={termsScrolledToEnd}
        onScrolledToEnd={() => setTermsScrolledToEnd(true)}
        accepted={termsAccepted}
        onAcceptedChange={setTermsAccepted}
        onClose={closeTermsModal}
        onConfirm={confirmTermsAndViewResults}
      />
    )}

    {footer}
  </>);

  // ── RESULTS ────────────────────────────────────────────────────────────────
  return wrap(<>
    <div style={{ display: "inline-flex", alignItems: "center", gap: "20px", marginBottom: "3rem" }}>
      {logo}
      <span style={{ fontFamily: F.mono, fontWeight: 600, fontSize: "12px", color: "#8892A4", letterSpacing: "0.28px" }}>
        Quantum Risk Check
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Eyebrow color={C.textMuted}>YOUR RESULTS</Eyebrow>
      <button onClick={downloadPDF} disabled={pdfLoading}
        style={{
          padding: "14px", background: C.border,
          border: `1px solid ${C.magenta}`, borderRadius: "4px",
          color: pdfLoading ? C.textSubtle : C.text,
          cursor: pdfLoading ? "default" : "pointer",
          fontSize: "13px", fontFamily: F.body,
          transition: "all 0.15s",
        }}
        onMouseEnter={e => { if (!pdfLoading) e.currentTarget.style.borderColor = C.text; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.magenta; }}
      >
        {pdfLoading ? "Generating PDF…" : "Download PDF"}
      </button>
    </div>

    {/* Score hero */}
    <div style={{
      borderTop: `2px solid ${C.text}`,
      borderBottom: `1px solid ${C.border}`,
      padding: "2rem 0", marginTop: "16px", marginBottom: "2rem",
      display: "flex", alignItems: "center", gap: "2.5rem", flexWrap: "wrap",
    }}>
      <ScoreRing score={scores.overall} />
      <div style={{ flex: 1, minWidth: "240px" }}>
        <Eyebrow color={C.textMuted}>QUANTUM RISK SCORE</Eyebrow>
        <div style={{
          fontSize: "32px", fontWeight: 600, color: ob.color,
          margin: "8px 0", fontFamily: F.display, letterSpacing: "-0.02em", lineHeight: 1.1,
        }}>
          {ob.label}
        </div>
        <div style={{
          display: "inline-block",
          background: C.bg, border: `1px solid ${ob.color}`,
          padding: "5px 12px", marginBottom: "12px",
        }}>
          <span style={{ fontSize: "10px", fontWeight: 500, color: ob.color, letterSpacing: "0.12em", fontFamily: F.mono }}>
            {ob.status}
          </span>
        </div>
        <p style={{ fontSize: "13px", color: C.textMuted, margin: 0, lineHeight: 1.55, fontFamily: F.body }}>
          {prep.cta}
        </p>
      </div>
    </div>

    {/* Dimension bars */}
    <div style={{ marginBottom: "2.5rem" }}>
      <Eyebrow color={C.text}>RISK EXPOSURE BY AREA</Eyebrow>
      <div style={{ marginTop: "1.25rem" }}>
        {SCORE_DIMENSIONS.map((dim, i) => (
          <DimBar key={dim.key} label={dim.label} score={scores[dim.key]} delay={i * 150} />
        ))}
      </div>
    </div>

    {/* Preparedness */}
    <div style={{
      background: prep.bg,
      border: `1px solid ${C.border}`,
      padding: "1rem 1.25rem",
      marginBottom: "2.5rem",
    }}>
      <Eyebrow color={C.text}>PREPAREDNESS</Eyebrow>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginTop: "10px", marginBottom: "10px" }}>
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: prep.dot, flexShrink: 0 }} />
        <span style={{ fontSize: "13px", fontWeight: 500, fontFamily: F.mono, letterSpacing: "0.06em", textTransform: "uppercase", color: prep.color }}>
          {prep.label}
        </span>
      </div>
      <p style={{ fontSize: "14px", color: C.text, margin: 0, lineHeight: 1.6, fontFamily: F.body }}>
        {prep.desc}
      </p>
    </div>

    {/* Findings */}
    {findings.length > 0 && (
      <div style={{ marginBottom: "2.5rem" }}>
        <Eyebrow color={C.text}>KEY FINDINGS</Eyebrow>
        <div style={{ marginTop: "1.25rem" }}>
          {findings.map((f, i) => {
            const c = findingColor(f.kind);
            return (
              <div key={i} style={{
                display: "flex", gap: "14px",
                padding: "16px 18px", background: C.bg,
                border: `1px solid ${C.border}`, borderLeftWidth: "3px",
                borderLeftStyle: "solid", borderLeftColor: c,
                marginBottom: "8px",
              }}>
                <span style={{ fontSize: "14px", color: C.text, lineHeight: 1.6, fontFamily: F.body }}>{f.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Regulatory advisories by selected jurisdictions */}
    {activeJurisdictions.length > 0 && (
      <div style={{
        background: C.surfaceLavender, padding: "1.5rem",
        marginBottom: "2.5rem", border: `1px solid ${C.lavender}`,
      }}>
        <Eyebrow color={C.text}>REGULATORY ADVISORIES</Eyebrow>
        <div style={{ marginTop: "12px" }}>
          {activeJurisdictions.map((key, idx) => {
            const j = JURISDICTIONS[key];
            return (
              <p key={key} style={{ margin: idx === activeJurisdictions.length - 1 ? 0 : "0 0 8px", fontSize: "13px", color: C.text, lineHeight: 1.6, fontFamily: F.body }}>
                <strong style={{ fontWeight: 600 }}>{j.name}</strong> ({j.authority}) — {j.instrument}. {j.note}
              </p>
            );
          })}
        </div>
      </div>
    )}

    {/* CTA */}
    <div style={{
      background: C.text, padding: "2rem",
      marginBottom: "1rem",
    }}>
      <Eyebrow color={C.teal}>NEXT STEP</Eyebrow>
      <h3 style={{
        fontFamily: F.display, fontSize: "22px", fontWeight: 500, color: C.bg,
        margin: "12px 0 12px", lineHeight: 1.3, letterSpacing: "-0.015em",
      }}>
        Book your complimentary Quantum Risk Briefing
      </h3>
      <p style={{ fontSize: "14px", color: C.lavender, margin: "0 0 1.5rem", lineHeight: 1.65, fontFamily: F.body }}>
        Your score is the starting point. In a focused 30-minute session, we'll walk through your results, identify your highest-priority gaps, and outline the specific steps your organisation should take next.
      </p>
      <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
        style={{
          display: "block", width: "100%", padding: "16px",
          background: C.teal, color: C.text,
          border: "none", borderRadius: "0",
          fontSize: "15px", fontWeight: 600, cursor: "pointer",
          textDecoration: "none", boxSizing: "border-box",
          textAlign: "center", fontFamily: F.body, letterSpacing: "0.01em",
        }}
        onClick={() => trackClick("cta_click", "book_briefing")}>
        Book a free 30-min briefing →
      </a>
      <p style={{ margin: "12px 0 0", fontSize: "12px", color: C.lavender, fontFamily: F.mono, letterSpacing: "0.04em", textAlign: "center" }}>
        No commitment required
      </p>
    </div>

    <button onClick={resetAll}
      style={{
        width: "100%", padding: "14px", background: C.bg,
        border: `1px solid ${C.border}`, borderRadius: "4px",
        color: C.textMuted, cursor: "pointer", fontSize: "13px", fontFamily: F.body,
        marginTop: "1rem",
      }}>
      Retake assessment
    </button>

    <p style={{
      marginTop: "2.5rem",
      paddingTop: "1.5rem",
      borderTop: `1px solid ${C.border}`,
      fontSize: "11px",
      color: C.textSubtle,
      lineHeight: 1.65,
      fontFamily: F.body,
    }}>
      <strong style={{ fontWeight: 600, color: C.textMuted }}>Disclaimer:</strong>{" "}
      This assessment is a high-level diagnostic tool intended for marketing and educational purposes only.
      The results are based on generalized benchmarks and should not be used as a substitute for
      professional security consulting or a technical cryptographic audit. Horizen Labs makes no
      representations or warranties regarding the accuracy of this assessment and assumes no liability
      for any actions taken in reliance on these results.
    </p>

    {footer}
  </>);
}
